import {
  workEntryIndicatesToolFailure,
  workEntryIndicatesToolNeutralStatus,
  workEntryIndicatesToolSuccess,
  workLogEntryIsToolLike,
  type WorkLogEntry,
} from "../../session-logic";
import { formatWorkspaceRelativePath } from "../../filePathDisplay";
import { cn } from "~/lib/utils";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  EyeIcon,
  GlobeIcon,
  HammerIcon,
  MessageCircleIcon,
  MinusIcon,
  SearchIcon,
  SparklesIcon,
  SquarePenIcon,
  TerminalIcon,
  WrenchIcon,
  XIcon,
  ZapIcon,
} from "lucide-react";
import { memo, useState, type KeyboardEvent } from "react";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { i18n } from "../../i18n";

function normalizeCompactToolLabel(value: string): string {
  return value.replace(/\s+(?:complete|completed)\s*$/i, "").trim();
}

export const WORK_ENTRY_PREVIEW_MAX_LENGTH = 240;

function capWorkEntryPresentationText(value: string): string {
  if (value.length <= WORK_ENTRY_PREVIEW_MAX_LENGTH) {
    return value;
  }
  return `${value.slice(0, WORK_ENTRY_PREVIEW_MAX_LENGTH - 1)}…`;
}

type WorkEntryIconName =
  | "check"
  | "circle-alert"
  | "eye"
  | "globe"
  | "hammer"
  | "message-circle"
  | "search"
  | "sparkles"
  | "square-pen"
  | "terminal"
  | "wrench"
  | "x"
  | "zap";

function WorkEntryIconSvg({ name, className }: { name: WorkEntryIconName; className: string }) {
  switch (name) {
    case "check":
      return <CheckIcon className={className} aria-hidden />;
    case "circle-alert":
      return <CircleAlertIcon className={className} aria-hidden />;
    case "eye":
      return <EyeIcon className={className} aria-hidden />;
    case "globe":
      return <GlobeIcon className={className} aria-hidden />;
    case "hammer":
      return <HammerIcon className={className} aria-hidden />;
    case "message-circle":
      return <MessageCircleIcon className={className} aria-hidden />;
    case "search":
      return <SearchIcon className={className} aria-hidden />;
    case "sparkles":
      return <SparklesIcon className={className} aria-hidden />;
    case "square-pen":
      return <SquarePenIcon className={className} aria-hidden />;
    case "terminal":
      return <TerminalIcon className={className} aria-hidden />;
    case "wrench":
      return <WrenchIcon className={className} aria-hidden />;
    case "x":
      return <XIcon className={className} aria-hidden />;
    case "zap":
      return <ZapIcon className={className} aria-hidden />;
  }
}

function workToneIcon(tone: WorkLogEntry["tone"]): {
  iconName: WorkEntryIconName;
  className: string;
} {
  if (tone === "error") {
    return {
      iconName: "circle-alert",
      className: "text-foreground/92",
    };
  }
  if (tone === "thinking") {
    return {
      iconName: "sparkles",
      className: "text-foreground/92",
    };
  }
  if (tone === "info") {
    return {
      iconName: "check",
      className: "text-muted-foreground",
    };
  }
  return {
    iconName: "zap",
    className: "text-foreground/92",
  };
}

function workEntryPreview(
  workEntry: Pick<WorkLogEntry, "actionSummary" | "detail" | "command" | "changedFiles">,
  workspaceRoot: string | undefined,
) {
  if (workEntry.actionSummary) return workEntry.actionSummary;
  if (workEntry.command) return workEntry.command;
  if (workEntry.detail) return workEntry.detail;
  if ((workEntry.changedFiles?.length ?? 0) === 0) return null;
  const [firstPath] = workEntry.changedFiles ?? [];
  if (!firstPath) return null;
  const displayPath = formatWorkspaceRelativePath(firstPath, workspaceRoot);
  return workEntry.changedFiles!.length === 1
    ? displayPath
    : `${displayPath} +${workEntry.changedFiles!.length - 1} more`;
}

function workEntryRawCommand(
  workEntry: Pick<WorkLogEntry, "command" | "rawCommand">,
): string | null {
  const rawCommand = workEntry.rawCommand?.trim();
  if (!rawCommand || !workEntry.command) {
    return null;
  }
  return rawCommand === workEntry.command.trim() ? null : rawCommand;
}

export function buildToolCallExpandedBody(
  workEntry: WorkLogEntry,
  workspaceRoot: string | undefined,
): string | null {
  const blocks: string[] = [];
  const appendUniqueBlock = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    if (trimmed && !blocks.includes(trimmed)) {
      blocks.push(trimmed);
    }
  };
  if (workEntry.itemType === "mcp_tool_call" && workEntry.toolData !== undefined) {
    appendUniqueBlock(`MCP call\n${JSON.stringify(workEntry.toolData, null, 2)}`);
  }
  appendUniqueBlock(workEntry.actionSummary);
  const raw = workEntryRawCommand(workEntry);
  if (raw?.trim()) {
    appendUniqueBlock(raw);
  } else if (workEntry.command?.trim()) {
    appendUniqueBlock(workEntry.command);
  }
  appendUniqueBlock(workEntry.detail);
  if (workEntry.detailTruncated) {
    appendUniqueBlock(i18n.t("activityGroup.toolDetailTruncated"));
  }
  const changedFiles = workEntry.changedFiles ?? [];
  if (changedFiles.length > 0) {
    appendUniqueBlock(
      changedFiles
        .map((filePath) => formatWorkspaceRelativePath(filePath, workspaceRoot))
        .join("\n"),
    );
  }
  return blocks.length > 0 ? blocks.join("\n\n") : null;
}

function hasVisibleWorkEntryText(value: string | null | undefined): boolean {
  return value !== null && value !== undefined && /\S/.test(value);
}

function workEntryHasExpandedBody(workEntry: WorkLogEntry): boolean {
  return (
    (workEntry.itemType === "mcp_tool_call" && workEntry.toolData !== undefined) ||
    hasVisibleWorkEntryText(workEntry.actionSummary) ||
    hasVisibleWorkEntryText(workEntry.rawCommand) ||
    hasVisibleWorkEntryText(workEntry.command) ||
    hasVisibleWorkEntryText(workEntry.detail) ||
    (workEntry.changedFiles?.some(hasVisibleWorkEntryText) ?? false)
  );
}

function workEntryIconName(workEntry: WorkLogEntry): WorkEntryIconName {
  if (
    workEntry.sourceActivityKind === "user-input.requested" ||
    workEntry.sourceActivityKind === "user-input.resolved"
  ) {
    return "message-circle";
  }
  if (workEntry.requestKind === "command") return "terminal";
  if (workEntry.requestKind === "file-read") return "eye";
  if (workEntry.requestKind === "file-change") return "square-pen";

  if (workEntry.itemType === "command_execution" || workEntry.command) {
    return "terminal";
  }
  if (workEntry.itemType === "file_read") return "eye";
  if (workEntry.itemType === "file_search") return "search";
  if (workEntry.itemType === "file_change" || (workEntry.changedFiles?.length ?? 0) > 0) {
    return "square-pen";
  }
  if (workEntry.itemType === "web_search") return "globe";
  if (workEntry.itemType === "image_view") return "eye";

  switch (workEntry.itemType) {
    case "mcp_tool_call":
      return "wrench";
    case "dynamic_tool_call":
    case "collab_agent_tool_call":
      return "hammer";
  }

  return workToneIcon(workEntry.tone).iconName;
}

function capitalizePhrase(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return value;
  }
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
}

function workEntryHeading(workEntry: WorkLogEntry): string {
  if (!workEntry.toolTitle) {
    return capitalizePhrase(normalizeCompactToolLabel(workEntry.label));
  }
  return capitalizePhrase(normalizeCompactToolLabel(workEntry.toolTitle));
}

const stopRowToggle = (event: { stopPropagation: () => void }) => event.stopPropagation();

interface WorkEntryPresentationProps {
  readonly workEntry: WorkLogEntry;
  readonly workspaceRoot: string | undefined;
  readonly turnSettled: boolean;
}

export const WorkEntryPresentation = memo(function WorkEntryPresentation({
  workEntry,
  workspaceRoot,
  turnSettled,
}: WorkEntryPresentationProps) {
  const isReasoning = workEntry.sourceActivityKind === "provider.reasoning";
  const isReasoningStreaming = isReasoning && workEntry.toolLifecycleStatus === "inProgress";
  const reasoningRevision =
    workEntry.presentationRevision ??
    `${workEntry.id}:${workEntry.toolLifecycleStatus ?? "unknown"}`;
  const expansionRevision = isReasoning ? reasoningRevision : "manual";
  const [manuallyExpandedRevision, setManuallyExpandedRevision] = useState<string | null>(null);
  const expanded = isReasoningStreaming || manuallyExpandedRevision === expansionRevision;
  const toggleManuallyExpanded = () =>
    setManuallyExpandedRevision((value) =>
      value === expansionRevision ? null : expansionRevision,
    );
  const iconConfig = workToneIcon(workEntry.tone);
  const showWarningIndicator = workEntry.sourceActivityKind === "runtime.warning";
  const entryIconName = showWarningIndicator ? "x" : workEntryIconName(workEntry);
  const heading = isReasoning
    ? i18n.t(isReasoningStreaming ? "activityGroup.reasoningInProgress" : "activityGroup.reasoning")
    : workEntryHeading(workEntry);
  const rawPreview = isReasoning ? null : workEntryPreview(workEntry, workspaceRoot);
  const preview =
    rawPreview &&
    rawPreview.length <= WORK_ENTRY_PREVIEW_MAX_LENGTH &&
    heading.length <= WORK_ENTRY_PREVIEW_MAX_LENGTH &&
    normalizeCompactToolLabel(rawPreview).toLowerCase() ===
      normalizeCompactToolLabel(heading).toLowerCase()
      ? null
      : rawPreview
        ? capWorkEntryPresentationText(rawPreview)
        : null;
  const displayHeading = capWorkEntryPresentationText(heading);
  const displayText = capWorkEntryPresentationText(
    preview ? `${displayHeading} - ${preview}` : displayHeading,
  );
  const canExpand = workEntryHasExpandedBody(workEntry);
  const canToggle = canExpand && !isReasoningStreaming;
  const expandedBody = expanded ? buildToolCallExpandedBody(workEntry, workspaceRoot) : null;
  const showFailedIndicator = workEntryIndicatesToolFailure(workEntry);
  const showDestructiveRowStyle =
    showFailedIndicator &&
    (workEntry.sourceActivityKind === "runtime.error" || !workLogEntryIsToolLike(workEntry));
  const iconWrapperClass = cn(
    "flex size-5 shrink-0 items-center justify-center",
    showWarningIndicator
      ? "text-destructive"
      : showDestructiveRowStyle
        ? "text-destructive"
        : isReasoning
          ? "text-muted-foreground/75"
          : workEntry.tone === "tool" || showFailedIndicator
            ? "text-muted-foreground/65"
            : iconConfig.className,
  );
  const headingClass = isReasoning
    ? "font-medium text-foreground/72"
    : showWarningIndicator
      ? "font-medium text-warning"
      : showDestructiveRowStyle
        ? "font-medium text-destructive"
        : "font-medium text-foreground/82";
  const indicatesNeutralStatus = workEntryIndicatesToolNeutralStatus(workEntry);
  const showRunningIndicator =
    !isReasoning && indicatesNeutralStatus && workEntry.toolLifecycleStatus === "inProgress";
  const showNeutralIndicator =
    !isReasoning && !turnSettled && indicatesNeutralStatus && !showRunningIndicator;
  const showSuccessIndicator =
    !isReasoning &&
    (workEntryIndicatesToolSuccess(workEntry) ||
      (turnSettled && indicatesNeutralStatus && !showRunningIndicator));
  const rowToggleProps = canToggle
    ? {
        role: "button" as const,
        tabIndex: 0 as const,
        "aria-label": displayText,
        "aria-expanded": expanded,
        onClick: toggleManuallyExpanded,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleManuallyExpanded();
          }
        },
      }
    : {};
  const renderHeaderContent = () => (
    <>
      <span className={iconWrapperClass}>
        <WorkEntryIconSvg
          name={entryIconName}
          className="block size-3.5 shrink-0 stroke-[1.8] opacity-80"
        />
      </span>
      <div className="min-w-0 flex-1 overflow-hidden">
        <p className="flex min-w-0 w-full items-baseline gap-1.5 text-[12px] leading-5">
          <span className={cn("min-w-0 shrink truncate", headingClass)}>{heading}</span>
          {preview && (
            <span className="min-w-0 flex-1 truncate text-muted-foreground/55">{preview}</span>
          )}
        </p>
      </div>
    </>
  );

  return (
    <div
      className={cn(
        "flex flex-col rounded-md px-0.5 py-0.5 transition-colors",
        canToggle &&
          (isReasoning
            ? "group/reasoning cursor-pointer focus-visible:outline-none"
            : "cursor-pointer hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"),
      )}
      {...rowToggleProps}
    >
      <div
        className={cn(
          "flex select-none items-center gap-1.5 transition-[background-color,opacity,translate] duration-200",
          isReasoning && "w-fit max-w-full rounded-md",
          isReasoning &&
            canToggle &&
            "-m-0.5 p-0.5 group-hover/reasoning:bg-accent/20 group-focus-visible/reasoning:ring-2 group-focus-visible/reasoning:ring-ring/70",
        )}
        data-reasoning-streaming={isReasoningStreaming || undefined}
      >
        <div
          className={cn(
            "flex min-w-0 items-center gap-1.5",
            isReasoning ? "max-w-full shrink overflow-hidden rounded-sm" : "flex-1",
            isReasoningStreaming && "reasoning-streaming-shimmer relative",
          )}
          data-reasoning-shimmer-target={isReasoning || undefined}
        >
          {renderHeaderContent()}
          {isReasoningStreaming ? (
            <div className="reasoning-streaming-shimmer-sweep" aria-hidden>
              <div className="reasoning-streaming-shimmer-highlight flex min-w-0 items-center gap-1.5">
                {renderHeaderContent()}
              </div>
            </div>
          ) : null}
        </div>
        <div
          className={cn(
            "flex shrink-0 items-center gap-px text-muted-foreground/55",
            isReasoning && "-ms-1",
          )}
        >
          <span
            className="flex size-4 shrink-0 items-center justify-center"
            aria-hidden={!canToggle}
          >
            {isReasoning && canExpand ? (
              <ChevronRightIcon
                className={cn(
                  "size-3 shrink-0 opacity-70 transition-transform duration-200",
                  expanded && "rotate-90",
                )}
                aria-hidden
              />
            ) : canToggle ? (
              <ChevronDownIcon
                className={cn(
                  "size-3 shrink-0 opacity-70 transition-transform duration-200",
                  expanded && "rotate-180",
                )}
                aria-hidden
              />
            ) : null}
          </span>
          {!isReasoning ? (
            <span className="flex size-4 shrink-0 items-center justify-center">
              {showFailedIndicator ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span
                        className="flex size-4 items-center justify-center"
                        aria-label="Tool call failed"
                      />
                    }
                  >
                    <XIcon className="block size-3 shrink-0 text-destructive" aria-hidden />
                  </TooltipTrigger>
                  <TooltipPopup>Failed</TooltipPopup>
                </Tooltip>
              ) : showSuccessIndicator ? (
                <Tooltip>
                  <TooltipTrigger
                    render={<span className="flex size-4 items-center justify-center" />}
                  >
                    <span className="inline-flex size-4 items-center justify-center">
                      <CheckIcon
                        className="block size-3 shrink-0 stroke-current"
                        stroke="currentColor"
                        aria-hidden
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipPopup>Completed</TooltipPopup>
                </Tooltip>
              ) : showRunningIndicator ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span
                        className="flex size-4 items-center justify-center"
                        aria-label="Tool call running"
                      />
                    }
                  >
                    <MinusIcon className="block size-3 shrink-0 opacity-70" aria-hidden />
                  </TooltipTrigger>
                  <TooltipPopup>Running</TooltipPopup>
                </Tooltip>
              ) : showNeutralIndicator ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span
                        className="flex size-4 items-center justify-center"
                        aria-label="Empty tool result"
                      />
                    }
                  >
                    <MinusIcon className="block size-3 shrink-0 opacity-70" aria-hidden />
                  </TooltipTrigger>
                  <TooltipPopup>Empty</TooltipPopup>
                </Tooltip>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>
      {expanded && canExpand && expandedBody ? (
        <div
          className="mt-1 ms-7 cursor-default border-s border-border/45 ps-3 pt-0.5"
          onClick={stopRowToggle}
          onPointerDown={stopRowToggle}
        >
          {isReasoning ? (
            <div className="max-h-64 cursor-text overflow-auto whitespace-pre-wrap break-words text-[13px] leading-6 text-foreground/78 select-text">
              {expandedBody}
            </div>
          ) : (
            <pre className="max-h-64 cursor-text overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-muted-foreground select-text">
              {expandedBody}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
});
